import { useState } from 'react';
import { Badge, Button, InlineError, Skeleton } from '../../components/ui';
import { PoDocumentView } from './PoDocumentView';
import type { PoDocument } from './PoDocumentView';

/**
 * Every PO issued for a job, and the way to open one.
 *
 * The paper trail Kyu asked for: "all information and data and files are there
 * in the dialog box" when you back-track an order. A PO is the contract
 * artefact of the job, so the file holds every revision — the current sheet and
 * each superseded one — rather than only whatever the record says today.
 *
 * Shared between the CG drawer and the supplier portal. Neither fetches for
 * the other: the two audiences call different RPCs and hand the rows in here,
 * which is what keeps the supplier payload an allow-list rather than a filter.
 */
export function PoHistory({
  documents,
  isPending,
  isError,
  showInForce = false,
}: {
  documents: PoDocument[];
  isPending: boolean;
  isError: boolean;
  /**
   * Renders the in-force revision OPEN, with the superseded ones listed
   * beneath it. The job page — TAP's and the factory's — passes this, because
   * "Read the PO" was a banner pointing at a bare revision-number link: the
   * document existed and the person told to read it could not find it. A
   * drawer or dialog leaves it false and keeps the compact list.
   */
  showInForce?: boolean;
}) {
  const [open, setOpen] = useState<PoDocument | null>(null);

  if (isPending) {
    return <Skeleton lines={2} label="Loading POs" />;
  }
  if (isError) {
    return <InlineError>The PO history could not be loaded.</InlineError>;
  }
  if (documents.length === 0) {
    // Not an error and not a blank space. An order before its PO is issued is
    // the ordinary case, and saying so beats an empty box that reads as broken.
    return <p className="job-order-muted">No PO has been issued for this job yet.</p>;
  }

  if (open !== null) {
    return <PoDocumentView doc={open} onClose={() => setOpen(null)} />;
  }

  const inForce = showInForce
    ? (documents.find((doc) => doc.superseded_at === null) ?? null)
    : null;
  const listed = inForce === null ? documents : documents.filter((doc) => doc !== inForce);

  return (
    <>
      {inForce === null ? null : <PoDocumentView doc={inForce} />}
      {listed.length === 0 ? null : inForce === null ? null : (
        <p className="job-order-muted">Superseded revisions — kept, watermarked when opened:</p>
      )}
      <ul className="po-history">
        {listed.map((doc) => (
          <li key={doc.revision}>
            <button type="button" className="po-history-open" onClick={() => setOpen(doc)}>
              {doc.label}
            </button>
            <span className="job-order-muted">{doc.issued_at.slice(0, 10)}</span>
            {doc.superseded_at === null ? (
              <Badge tone="success">In force</Badge>
            ) : (
              <Badge tone="neutral">Superseded</Badge>
            )}
            {doc.reason === '' ? null : <span className="job-order-muted">{doc.reason}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

/** The current sheet, for a caller that only wants to offer one download. */
export function currentPo(documents: PoDocument[]): PoDocument | null {
  return documents.find((doc) => doc.superseded_at === null) ?? null;
}

export function PoHistoryActions({ documents }: { documents: PoDocument[] }) {
  const [open, setOpen] = useState(false);
  const live = currentPo(documents);
  if (live === null) {
    return null;
  }
  if (open) {
    return <PoDocumentView doc={live} onClose={() => setOpen(false)} />;
  }
  return (
    <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
      Open PO {live.label}
    </Button>
  );
}
